import { DatePipe } from '@angular/common';
import { Component, forwardRef, Input, OnChanges, SimpleChanges, Output, EventEmitter } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import moment from 'moment';
import { firstValueFrom } from 'rxjs';
import { AvaliablePeriodsDTO } from 'src/app/models/dtos/operational-options/avaliable-periods-dto';
import { MovementPlanAppointmentService } from 'src/app/services/movements/movement-plan-appointment.service';
import { MessageUtil } from 'src/app/utils/message.util';
import { OperationalConfigService } from 'src/app/services/config/operational-config.service';

@Component({
  selector: 'app-schedule-date-mv',
  templateUrl: './schedule-date.component.html',
  styleUrls: ['./schedule-date.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ScheduleDateComponent),
      multi: true
    }
  ]
})
export class ScheduleDateComponent implements OnChanges, ControlValueAccessor {
  private readonly dateFormatAvaliablePeriodsValue: string = 'YYYY-MM-DDTHH:mm';
  private readonly labelDateFormatAvaliablePeriods: string = 'HH:mm';

  @Input()
  readonly minDate: Date;
  @Input()
  readonly boat: { id: number };
  @Input()
  readonly title: string;
  @Input()
  readonly isReturnDate: boolean;

  @Input()
  started

  @Output()
  dateTimeSelected: EventEmitter<Date> = new EventEmitter<Date>();

  manualHourRequired: boolean = false;
  manualHour: boolean = false;

  availablePeriods: { label: string, value: Date, isOld?: boolean }[] = [];
  selectedPeriods: Date;
  oldValueFromEdit: Date | null = null;

  newDate: Date = null;
  newDateTime: Date = null;
  dateManual: any;
  disabled: boolean = false;

  private datePipe = new DatePipe('pt-BR');

  private onChange: any = () => { };
  writeValue(obj: Date | null): void {
    if (obj) {
      this.oldValueFromEdit = obj;
      const dateformated = moment(obj).format(this.dateFormatAvaliablePeriodsValue)
      const result = this.availablePeriods.filter(({value}) => moment(value).format(this.dateFormatAvaliablePeriodsValue) === dateformated);
      if (result.length <= 0) {
        this.availablePeriods.unshift({label: this.datePipe.transform(obj, this.labelDateFormatAvaliablePeriods), value: obj})
        this.newDate = obj;
        this.newDateTime = obj;
        this.selectedPeriods = obj;
      } else {
        this.oldValueFromEdit = result[0].value;
        this.newDate = result[0].value;
        this.newDateTime = result[0].value;
        this.selectedPeriods = result[0].value;
      }
      
      if (this.boat) {
        this.loadAvaliablePeriods(this.newDate).catch(() => {});
      }
    } else {
      this.newDate = this.minDate;
    }
  }
  registerOnChange(fn: any): void { this.onChange = fn };
  registerOnTouched(_fn: any): void { };
  setDisabledState?(isDisabled: boolean): void { this.disabled = isDisabled };

  constructor(
    private readonly movementPlanAppointmentService: MovementPlanAppointmentService,
    private messageUtil: MessageUtil,
    private operationalConfigService: OperationalConfigService
  ) { }

  async ngOnChanges(changes: SimpleChanges): Promise<void> {
    if (changes.minDate && changes.minDate.currentValue && changes.minDate.currentValue !== changes.minDate.previousValue ) {
      const newMinDate = this.minDate;
      if (this.newDateTime instanceof Date) {
        if (newMinDate.getTime() > this.newDateTime.getTime()) {
          this.newDateTime = null;
          this.newDate = new Date(newMinDate.getTime());
          await this.changeDateByCalendar(this.newDate, false);
        }
      } else if (this.newDate instanceof Date) {
        if (newMinDate.getTime() > this.newDate.getTime()) {
          this.newDate = new Date(newMinDate.getTime());
          await this.changeDateByCalendar(this.newDate, false);
        }
      } else {
        this.newDate = new Date(newMinDate);
        await this.changeDateByCalendar(this.newDate, false);
      }
    }
    if (changes.boat && changes.boat.currentValue && changes.boat.currentValue !== changes.boat.previousValue ) {
      if (this.newDate) {
        await this.loadAvaliablePeriods(this.newDate);
      }
    }
  }

  private async loadAvaliablePeriods(newDate: Date): Promise<void> {
    if (!this.boat || !this.minDate) {
      return;
    }

    let date: Date;
    if (this.isReturnDate) {
      date = newDate.getTime() < this.minDate.getTime() ? this.minDate : newDate;
    } else {
      const today = new Date();
      const isToday = this.areSameCalendarDay(newDate, today);
      const enableOrderOfArrival = await this.operationalConfigService.isEnableOrderOfArrival();
      date = (isToday || enableOrderOfArrival)
        ? new Date(new Date(newDate).setHours(0, 0, 0, 0))
        : new Date(newDate);
    }

    try {
      const { periods }: AvaliablePeriodsDTO = await firstValueFrom(
        this.movementPlanAppointmentService.findAvaliablePeriods(this.boat.id, date, this.isReturnDate)
      );
      await this.setAvaliablePeriods(periods, date);
    } catch (error: any) {
      await this.setAvaliablePeriods([], date);
      if (!this.manualHour && this.availablePeriods.length <= 0) {
        this.sendNewDateTime(null);
        const exception = error?.error?.data?.exception;
        if (exception) {
          this.messageUtil.generateMessage(exception.type, exception.title, exception.message);
        }
      }
    }
  }

  async setAvaliablePeriods(values: Date[], date: Date): Promise<void> {
    const isFutureDate = this.isFutureCalendarDay(date);

    const enableOrderOfArrival = await this.operationalConfigService.isEnableOrderOfArrival();
    
    if (values.length === 0 && isFutureDate && !enableOrderOfArrival) {
      const baseDate = new Date(date);
      baseDate.setHours(8, 0, 0, 0);
      
      const defaultPeriods: Date[] = [];
      for (let hour = 8; hour <= 18; hour++) {
        const periodDate = new Date(baseDate);
        periodDate.setHours(hour);
        defaultPeriods.push(periodDate);
      }
      values = defaultPeriods;
    }
    
    if (this.oldValueFromEdit) {
      const day = moment(date).format('YYYY-MM-DD');
      if (moment(this.oldValueFromEdit).format('YYYY-MM-DD') === day) {
        values.unshift(this.oldValueFromEdit);
      }
    }
    
    this.availablePeriods = values.map((date: Date) => ({ 
      label: this.datePipe.transform(date, this.labelDateFormatAvaliablePeriods), 
      value: new Date(date) 
    }));
  }

  moveDay(numDay: number): void {
    if (numDay) {
      const newDay = new Date(this.newDate ?? new Date());
      newDay.setDate(newDay.getDate() + numDay);
      newDay.setHours(0, 0, 0);
      this.newDate = new Date(newDay.getTime());
      this.changeDateByCalendar(this.newDate, true);
    }
  }

  isMinDay() : boolean {
    const minDate = new Date(this.minDate);
    const date = new Date(this.newDate);
    if (!isNaN(minDate.getTime()) && !isNaN(date.getTime())) {
      const minMidnight = new Date(minDate.setHours(0, 0, 0, 0)).getTime();
      const currentMidnight = new Date(date.setHours(0, 0, 0, 0)).getTime();
      return currentMidnight <= minMidnight;
    }
    return true;
  }

  async selectManualDateTime(event: any, time: string): Promise<void> {
    if (time) {
      const [hours, minutes] = time.split(':');
      const newDate = new Date(this.newDate);
      newDate.setHours(parseInt(hours), parseInt(minutes));
      if (!isNaN(newDate.getTime())) {
        this.sendNewDateTime(newDate);
        
        const enableOrderOfArrival = await this.operationalConfigService.isEnableOrderOfArrival();
        
        if (!this.isReturnDate) {
          const isFutureDate = this.isFutureCalendarDay(newDate);
          
          if (isFutureDate && !enableOrderOfArrival) {
            this.dateTimeSelected.emit(newDate);
          } else if (!isFutureDate) {
            this.dateTimeSelected.emit(newDate);
          }
        }
      }
    }
  }

  async selectDateTime(event: { originalEvent: any, value: Date }): Promise<void> {
    this.sendNewDateTime(event.value);
    
    const enableOrderOfArrival = await this.operationalConfigService.isEnableOrderOfArrival();
    
     if (!this.isReturnDate) {
      const selectedDate = event.value;
      const isFutureDate = this.isFutureCalendarDay(selectedDate);
      
      if (isFutureDate && !enableOrderOfArrival) {
        this.dateTimeSelected.emit(selectedDate);
      } else if (!isFutureDate) {
        this.dateTimeSelected.emit(selectedDate);
      }
    }
  }

  async changeDateByCalendar(date: Date, isUserAction: boolean = true) {
    if (this.manualHour && this.newDateTime) {
      const hours = this.newDateTime.getHours();
      const min = this.newDateTime.getMinutes();
      this.sendNewDateTime(new Date(date.setHours(hours, min)));
    } else {
      this.newDate = new Date(date);
    }
    
    await this.loadAvaliablePeriods(date);
    
    const enableOrderOfArrival = await this.operationalConfigService.isEnableOrderOfArrival();
    
    const isFutureDate = this.isFutureCalendarDay(date);
    
    if (isUserAction && !this.isReturnDate && isFutureDate) {
      if (this.availablePeriods.length > 0) {
        await this.selectDateTime({ originalEvent: null, value: this.availablePeriods[0].value });
      } else if (!enableOrderOfArrival) {
        const defaultDate = new Date(date);
        defaultDate.setHours(8, 0, 0, 0);
        this.sendNewDateTime(defaultDate);
        this.dateTimeSelected.emit(defaultDate);
      }
    }
  }

  sendNewDateTime(date: Date): void {
    this.newDateTime = date;
    this.selectedPeriods = date;
    this.onChange(date);
  }

  onChangeManual(event: {originalEvent: PointerEvent, checked: boolean}) {
    if (event.originalEvent.isTrusted) {
      if (event.checked) {
        this.sendNewDateTime(null);
      } else {
        this.dateManual = null;
        this.sendNewDateTime(this.dateManual);
      }
    }
  }

  private areSameCalendarDay(a: Date, b: Date): boolean {
    return a.getDate() === b.getDate() && a.getMonth() === b.getMonth() && a.getFullYear() === b.getFullYear();
  }

  private isFutureCalendarDay(date: Date): boolean {
    const tomorrowDate = new Date(date.getTime() + 24 * 60 * 60 * 1000);
    return tomorrowDate.getDate() > new Date().getDate();
  }
}
